import React from 'react';

// Base Skeleton Component
interface SkeletonProps {
  className?: string;
  variant?: 'text' | 'circular' | 'rectangular';
  width?: string;
  height?: string;
  animation?: 'pulse' | 'wave';
}

export const Skeleton: React.FC<SkeletonProps> = ({
  className = '',
  variant = 'rectangular',
  width = '100%',
  height = '20px',
  animation = 'pulse',
}) => {
  const variantStyles = {
    text: 'rounded',
    circular: 'rounded-full',
    rectangular: 'rounded-lg',
  };

  const animationStyles = {
    pulse: 'animate-pulse',
    wave: 'animate-shimmer bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200',
  };

  return (
    <div
      className={`bg-gray-200 ${variantStyles[variant]} ${animationStyles[animation]} ${className}`}
      style={{ width, height }}
    />
  );
};

// Card Skeleton for loading card states
export const CardSkeleton: React.FC<{ showFooter?: boolean }> = ({ showFooter = true }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-4">
      {/* Header Skeleton */}
      <div className="space-y-2">
        <Skeleton width="60%" height="24px" />
        <Skeleton width="40%" height="16px" />
      </div>
      
      {/* Body Skeleton */}
      <div className="space-y-2">
        <Skeleton width="100%" height="16px" />
        <Skeleton width="90%" height="16px" />
        <Skeleton width="95%" height="16px" />
      </div>
      
      {/* Footer Skeleton */}
      {showFooter && (
        <div className="flex gap-2 pt-2">
          <Skeleton width="80px" height="36px" />
          <Skeleton width="80px" height="36px" />
        </div>
      )}
    </div>
  );
};

// List Skeleton for loading list states
export const ListSkeleton: React.FC<{ rows?: number }> = ({ rows = 5 }) => {
  return (
    <div className="space-y-3">
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="flex items-center gap-4 p-4 bg-white rounded-lg border border-gray-200">
          <Skeleton variant="circular" width="48px" height="48px" />
          <div className="flex-1 space-y-2">
            <Skeleton width="60%" height="20px" />
            <Skeleton width="40%" height="16px" />
          </div>
          <Skeleton width="24px" height="24px" />
        </div>
      ))}
    </div>
  );
};

// Table Skeleton for loading table states
export const TableSkeleton: React.FC<{ rows?: number; columns?: number }> = ({ 
  rows = 5, 
  columns = 4 
}) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gray-50 border-b border-gray-200 p-4">
        <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
          {Array.from({ length: columns }).map((_, index) => (
            <Skeleton key={index} height="20px" />
          ))}
        </div>
      </div>
      
      {/* Rows */}
      <div className="divide-y divide-gray-200">
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <div key={rowIndex} className="p-4">
            <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
              {Array.from({ length: columns }).map((_, colIndex) => (
                <Skeleton key={colIndex} height="16px" />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Dashboard Metric Skeleton
export const MetricCardSkeleton: React.FC = () => {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-start justify-between">
        <div className="space-y-2 flex-1">
          <Skeleton width="50%" height="16px" />
          <Skeleton width="70%" height="32px" />
        </div>
        <Skeleton variant="circular" width="48px" height="48px" />
      </div>
      <div className="mt-4">
        <Skeleton width="100%" height="8px" />
      </div>
    </div>
  );
};

// Form Input Skeleton
export const FormSkeleton: React.FC<{ fields?: number }> = ({ fields = 3 }) => {
  return (
    <div className="space-y-4">
      {Array.from({ length: fields }).map((_, index) => (
        <div key={index} className="space-y-2">
          <Skeleton width="30%" height="16px" />
          <Skeleton width="100%" height="40px" />
        </div>
      ))}
      <div className="flex gap-2 pt-4">
        <Skeleton width="100px" height="40px" />
        <Skeleton width="100px" height="40px" />
      </div>
    </div>
  );
};

export default Skeleton;
